import { useTranslation } from "react-i18next";
import { Typography, Box, Skeleton } from "@mui/material/";

import { Insight } from "../../../types";
import { colors } from "../../../theme/palette";
import { convertToCamelCase } from "../../../utils/formatKeys";

export const InsightCard = ({
  insight: { insightNumber, insightTitle, insightValue, isPositive, insightDuration, isComingSoon },
}: {
  insight: Insight;
}) => {
  const normalizedInsightTitle = convertToCamelCase(insightTitle);
  const normalizedInsightDuration = insightDuration ? convertToCamelCase(insightDuration) : undefined;

  return isComingSoon
    ? ComingSoonCard(normalizedInsightTitle)
    : NormalInsightCard({
        insightNumber,
        insightTitle: normalizedInsightTitle,
        insightValue,
        isPositive,
        insightDuration: normalizedInsightDuration,
      });
};

const ComingSoonCard = (insightTitle: string) => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.cardContainer(true)}>
      <Typography variant="h5Bold" sx={{ color: colors.Green100, opacity: "0.55" }}>
        {t(`pages.dashboard.${insightTitle}`)}
      </Typography>
      <Typography variant="body14Regular" sx={{ fontWeight: 500, color: colors.Green100, opacity: "0.55" }}>
        {t(`pages.dashboard.keepWaitingToTryIt`)}
      </Typography>
      <Typography variant="body12Regular" sx={{ color: colors.Green100, opacity: "0.55" }}>
        {t(`pages.dashboard.comingSoon`)}
      </Typography>
    </Box>
  );
};

const NormalInsightCard = ({
  insightNumber,
  insightTitle,
  insightValue,
  isPositive,
  insightDuration,
}: {
  insightNumber?: number;
  insightTitle: string;
  insightValue?: string;
  isPositive?: boolean;
  insightDuration?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.cardContainer(false)}>
      <Typography variant="h5Bold" sx={{ color: colors.Black100 }}>
        {insightNumber}
      </Typography>
      <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
        {t(`pages.dashboard.${insightTitle}`)}
      </Typography>
      <Box sx={{ display: "flex", columnGap: "5px", alignItems: "center" }}>
        {insightValue && (
          <Typography
            variant="body13Bold"
            sx={
              insightValue !== "0" && insightValue !== "+0%" && insightValue !== "0%"
                ? isPositive
                  ? { color: colors.Green100 }
                  : { color: colors.Red100 }
                : null
            }
          >
            {insightValue === "0" || insightValue === "+0%" || insightValue === "0%"
              ? insightValue
              : isPositive
              ? `+${insightValue?.slice(0, 6)}`
              : `${insightValue?.slice(0, 6)}`}
          </Typography>
        )}
        <Typography variant="body12Regular" sx={{ color: colors.Dark800 }}>
          {t(`pages.dashboard.${insightDuration}`)}
        </Typography>
      </Box>
    </Box>
  );
};

export const InsightLoadingCard = () => {
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "8px",
        background: colors.White,
        border: `1px solid ${colors.Gray100}`,
        padding: "18px 12.2px  18px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        alignItems: "flex-start",
      }}
    >
      <Typography variant="h5Bold">
        <Skeleton variant="rounded" width="59px" animation="wave" />
      </Typography>
      <Typography variant="body14Regular">
        <Skeleton variant="rounded" width="140px" animation="wave" />
      </Typography>
      <Box sx={{ display: "flex", columnGap: "5px", alignItems: "center" }}>
        <Typography variant="body13Bold">
          <Skeleton variant="rounded" width="32px" animation="wave" />
        </Typography>
        <Typography variant="body12Regular">
          <Skeleton variant="rounded" width="66px" animation="wave" />
        </Typography>
      </Box>
    </Box>
  );
};

const styles = {
  cardContainer: (isComingSoon: boolean) => ({
    width: "100%",
    borderRadius: "8px",
    background: colors.White,
    border: `1px solid ${isComingSoon ? colors.SecondaryMint : colors.Gray100}`,
    padding: "18px 12.2px  18px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    alignItems: "flex-start",
  }),
};
